import { SubmitState } from '@n2-common/components/MultiStageOnePageWizard/types';
import { Offer } from '@n2-common/serverGeneratedTypings/offer';
import { getExtendedOfferStatusForSignPage, startSignNorwegianLoans } from '@n2-common/services/legacyApplicationApiService';
import { SubmitProgressBox } from '@n2-common/ui/SubmitProgress';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { navigateToStep, ApplicationPaths } from '../../../../common/applicationPages/ApplicationFormsHelpers';
import { useApplicationIdContext } from '../../../../common/components/ApplicationContext';
import { SignCommonComponent, useApplicationSignContext } from '../../../../common/components/Sign/SignCommon';
import { trackCoreBankEvent, trackSectionView } from '../../../../common/lib/gtm';
import { ApplicationSignStatusModel } from '../../../../common/serverGeneratedTypings/applicationSignStatusModel';
import { ApplicationStatusModel } from '../../../../common/serverGeneratedTypings/applicationStatusModel';
import { ActionList, Button, ButtonRowContainer, Headline, Paragraph, useDismissableErrorMessage } from '../../../../common/ui';
import { Action, ActionState } from '../../../../common/ui/ActionList';
import { navigateToCorrectStep } from '../../App';
import { OfferBox } from '../Offer/OfferBox';
import { O1DocumentationContextProvider, useDocumentationContext } from '@n2-common/components/UploadDocuments';
import { DocumentRequirement } from '@n2-common/components/UploadDocuments';
import { getDocumentationState } from '@n2-common/applicationPages/applicationStatusHelpers';
import { CmsNotification } from '@n2-common/components';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';

type SignActions = 'UploadDocuments' | 'Sign' | 'AccountNumber';

const getAccountNumberState = (applicationStatus: ApplicationStatusModel | null): ActionState => {
    if (applicationStatus === null) {
        return 'pending';
    }
    return applicationStatus.AccountNumberForPayout !== null && applicationStatus.AccountNumberForPayout.length > 0 ? 'completed' : 'ready';
};

const getSignState = (applicationStatus: ApplicationStatusModel | null, signStatus: ApplicationSignStatusModel | null): ActionState => {
    if (applicationStatus === null || signStatus === null) {
        return 'pending';
    }
    if (applicationStatus.IsMainApplicant) {
        return signStatus.MainApplicantHasSigned ? 'final' : 'ready';
    } else {
        return signStatus.CoApplicantHasSigned ? 'final' : 'ready';
    }
};

export async function startSign(applicationId: string, applicantId: string, setSubmitState: Dispatch<SetStateAction<SubmitState>>) {
    setSubmitState('Processing');
    try {
        const res = await startSignNorwegianLoans(applicationId, applicantId);
        if (res.Error) {
            setSubmitState('Error');
            logErrorToServerside('startSignNorwegianLoans returned error', applicationId, applicantId, res);
            return false;
        }
        if (!res.Url) {
            logErrorToServerside('startSignNorwegianLoans returned empty URL', applicationId, applicantId, res);
            return false;
        }
        location.assign(res.Url);
        return true;
    } catch (err) {
        setSubmitState('Error');
        logErrorToServerside('Error invoking startSignNorwegianLoans', applicationId, applicantId, err);
        return false;
    }
}

export function Sign(): JSX.Element {
    return (
        <SignCommonComponent navigateToCorrectStepFunc={navigateToCorrectStep}>
            <O1DocumentationContextProvider>
                <SignInner />
            </O1DocumentationContextProvider>
        </SignCommonComponent>
    );
}

function SignInner(): JSX.Element {
    const navigate = useNavigate();
    const { applicantId, applicationId, isMainApplicant, applicationStatus, applicationSignStatus } = useApplicationSignContext();
    const { setErrorMessage, ErrorOutput } = useDismissableErrorMessage();
    const { requirements } = useDocumentationContext();
    const { t } = useTranslation('common');

    const [submitState, setSubmitState] = useState<SubmitState>('NotSent');

    useEffect(() => {
        document.title = 'Signering';
    }, []);
    const [currentOffer, setCurrentOffer] = useState<Offer | null>(null);

    const { country, productType } = useApplicationIdContext();

    useEffect(() => {
        getExtendedOfferStatusForSignPage(applicationId, applicantId).then((res) => {
            setCurrentOffer(res.Offer);
        });
    }, [applicationId, applicantId]);

    const combinedOffer: Offer | null = useMemo(() => {
        if (!currentOffer || !applicationStatus) {
            return null;
        }
        return { ...currentOffer, LoanAmount: applicationStatus.OfferedAmount };
    }, [currentOffer, applicationStatus]);

    useEffect(() => {
        trackSectionView(productType, country, '4.0', 'Sign overview');
    }, [country, productType]);

    const actions = useMemo(() => {
        return getSignActionsState(requirements, applicationStatus, applicationSignStatus, isMainApplicant, t);
    }, [requirements, applicationSignStatus, applicationStatus, isMainApplicant, t]);

    useEffect(() => {
        const remainder = actions.filter((m) => m.state !== 'completed' && m.state !== 'final');
        if (remainder.length > 0) {
            return;
        }
        if (!applicationStatus) {
            return;
        }
        trackCoreBankEvent(productType, country, 'application_complete', undefined, applicationId, applicationStatus?.IsAgentApplication);
    }, [country, productType, actions, applicationId, applicationStatus]);

    function doSignAction(taskType: SignActions): void {
        switch (taskType) {
            case 'Sign':
                startSign(applicationId, applicantId, setSubmitState).then((started) => {
                    if (!started) {
                        setErrorMessage(t<string>('Sign.SignDetails.FailedStartSigning'));
                    }
                });
                break;
            case 'UploadDocuments':
                navigateToStep(ApplicationPaths.uploadDocuments, applicationId, navigate, applicantId);
                break;
            case 'AccountNumber':
                navigateToStep(ApplicationPaths.setAccountNumber, applicationId, navigate, applicantId);
                break;
        }
    }

    return (
        <>
            {' '}
            <CmsNotification cmsKey={`sign_loan_no`} />
            <Headline>
                {t<string>('Sign.Headline')} {isMainApplicant === false ? t<string>('CoApplicant') : ''}
            </Headline>
            <SubmitProgressBox
                state={submitState}
                submitText={t<string>('FormSubmitButton.Submitted')}
                processingText={t<string>('FormSubmitButton.Processing')}
            ></SubmitProgressBox>
            <Paragraph>{t<string>('Sign.Intro')}</Paragraph>
            <ActionList
                ui="compact"
                actions={actions}
                execute={(taskType) => doSignAction(taskType)}
                completedPlaceholder={
                    <ButtonRowContainer>
                        <Button onClick={() => navigateToStep(ApplicationPaths.applicantActionsCompleted, applicationId, navigate, applicantId)}>
                            {' '}
                            {t<string>('Sign.Complete')}
                        </Button>
                    </ButtonRowContainer>
                }
            />
            {combinedOffer && <OfferBox offer={combinedOffer} />}
            <ErrorOutput />
        </>
    );
}

export function getSignActionsState(
    requirements: DocumentRequirement[],
    applicationStatus: ApplicationStatusModel | null,
    applicationSignStatus: ApplicationSignStatusModel | null,
    isMainApplicant: boolean | null,
    t: TFunction
) {
    const documentState = getDocumentationState(requirements);
    const signState = getSignState(applicationStatus, applicationSignStatus);
    const accountNumberState = getAccountNumberState(applicationStatus);
    const lst: Action<SignActions>[] = [];

    lst.push({
        name: t<string>('Sign.AccountNumber.TaskName'),
        description:
            accountNumberState === 'completed'
                ? `${t<string>('Sign.AccountNumber.TaskDescriptionCompleted')} ${applicationStatus?.AccountNumberForPayout} `
                : t<string>('Sign.AccountNumber.TaskDescription'),
        parameter: 'AccountNumber',
        state: accountNumberState,
        actionName: 'Detaljer',
        iconName: 'cashIcon',
    });

    if (requirements.length > 0) {
        lst.push({
            name: documentState === 'completed' ? t<string>('Sign.Documents.TaskNameCompleted') : t<string>('Sign.Documents.TaskName'),
            description:
                documentState === 'completed' ? t<string>('Sign.Documents.TaskDescriptionCompleted') : t<string>('Sign.Documents.TaskDescription'),
            parameter: 'UploadDocuments',
            state: documentState,
            actionName: 'Detaljer',
            iconName: 'cashIcon',
        });
    }
    if (accountNumberState === 'completed') {
        lst.push({
            name: t<string>('Sign.SignDetails.TaskName'),
            description: signState === 'final' ? t<string>('Sign.SignDetails.TaskDescriptionSigned') : t<string>('Sign.SignDetails.TaskDescription'),
            parameter: 'Sign',
            state: signState,
            actionName: 'Start signering',
            iconName: 'signIcon',
        });
    }
    return lst;
}
